import {
  File,
  FileText,
  FileSpreadsheet,
  FileJson,
  FileArchive,
  Presentation,
  FileCode,
  FileImage,
  Film,
  Music,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { fileExtensionFrom, isDirectVideoUrl } from './blocks/blockUtils';

/** YouTube / Vimeo / direct file — same behavior in public + embed views */
export function VideoBlockPreview({ url, caption }) {
  if (!url) return null;

  const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (youtubeMatch) {
    return (
      <div className="mb-6">
        <div className="aspect-video">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeMatch[1]}`}
            className="w-full h-full rounded-lg"
            allowFullScreen
            title="YouTube video"
          />
        </div>
        {caption ? <p className="mt-2 text-sm text-center text-slate-500">{caption}</p> : null}
      </div>
    );
  }

  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return (
      <div className="mb-6">
        <div className="aspect-video">
          <iframe
            src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
            className="w-full h-full rounded-lg"
            allowFullScreen
            title="Vimeo video"
          />
        </div>
        {caption ? <p className="mt-2 text-sm text-center text-slate-500">{caption}</p> : null}
      </div>
    );
  }

  if (isDirectVideoUrl(url)) {
    return (
      <div className="mb-6">
        <video src={url} controls className="w-full rounded-lg">
          Your browser does not support the video tag.
        </video>
        {caption ? <p className="mt-2 text-sm text-center text-slate-500">{caption}</p> : null}
      </div>
    );
  }

  /* Non-direct URL: try native video (works for some CDN links); user can use Embed block for arbitrary pages */
  return (
    <div className="mb-6">
      <video src={url} controls className="w-full rounded-lg">
        Your browser does not support the video tag.
      </video>
      {caption ? <p className="mt-2 text-sm text-center text-slate-500">{caption}</p> : null}
    </div>
  );
}

function pickFileIcon(ext) {
  const audio = new Set(['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac']);
  if (audio.has(ext)) {
    return { Icon: Music, box: 'bg-violet-100 dark:bg-violet-900/30', color: 'text-violet-600 dark:text-violet-400' };
  }
  if (ext === 'pdf') {
    return { Icon: FileText, box: 'bg-red-100 dark:bg-red-900/30', color: 'text-red-600 dark:text-red-400' };
  }
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) {
    return { Icon: FileText, box: 'bg-blue-100 dark:bg-blue-900/30', color: 'text-blue-600 dark:text-blue-400' };
  }
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) {
    return { Icon: FileSpreadsheet, box: 'bg-emerald-100 dark:bg-emerald-900/30', color: 'text-emerald-600 dark:text-emerald-400' };
  }
  if (['ppt', 'pptx', 'odp'].includes(ext)) {
    return { Icon: Presentation, box: 'bg-orange-100 dark:bg-orange-900/30', color: 'text-orange-600 dark:text-orange-400' };
  }
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
    return { Icon: FileImage, box: 'bg-pink-100 dark:bg-pink-900/30', color: 'text-pink-600 dark:text-pink-400' };
  }
  if (['zip', 'rar', '7z', 'tar', 'gz', 'tgz'].includes(ext)) {
    return { Icon: FileArchive, box: 'bg-amber-100 dark:bg-amber-900/30', color: 'text-amber-700 dark:text-amber-400' };
  }
  if (['json', 'xml', 'yaml', 'yml'].includes(ext)) {
    return { Icon: FileJson, box: 'bg-slate-200 dark:bg-slate-700', color: 'text-slate-700 dark:text-slate-200' };
  }
  if (['js', 'jsx', 'ts', 'tsx', 'py', 'rb', 'go', 'rs', 'java', 'c', 'cpp', 'h', 'cs', 'php', 'sql', 'sh'].includes(ext)) {
    return { Icon: FileCode, box: 'bg-indigo-100 dark:bg-indigo-900/30', color: 'text-indigo-600 dark:text-indigo-400' };
  }
  if (['mp4', 'webm', 'mov', 'm4v', 'ogv'].includes(ext)) {
    return { Icon: Film, box: 'bg-fuchsia-100 dark:bg-fuchsia-900/30', color: 'text-fuchsia-600 dark:text-fuchsia-400' };
  }
  if (['md', 'txt', 'log'].includes(ext)) {
    return { Icon: FileText, box: 'bg-slate-100 dark:bg-slate-800', color: 'text-slate-600 dark:text-slate-300' };
  }
  return { Icon: File, box: 'bg-slate-100 dark:bg-slate-800', color: 'text-slate-500 dark:text-slate-400' };
}

export function FileBlockPreview({ url, filename, size, variant = 'public' }) {
  if (!url) return null;
  const name = filename || url.split('/').pop()?.split('?')[0] || 'File';
  const ext = fileExtensionFrom(name || url);
  const { Icon, box, color } = pickFileIcon(ext);
  const isEmbed = variant === 'embed';

  return (
    <div
      className={cn(
        'mb-4 p-4 border border-slate-200 dark:border-slate-700 rounded-lg flex items-center gap-3',
        isEmbed ? '' : 'bg-slate-50 dark:bg-slate-800'
      )}
    >
      <div className={cn('p-2 rounded shrink-0', box)}>
        <Icon className={cn('w-8 h-8', color)} aria-hidden />
      </div>
      <div className="flex-1 min-w-0">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-slate-900 dark:text-white truncate hover:underline block"
        >
          {name}
        </a>
        {size ? <p className="text-sm text-slate-500">{size}</p> : null}
      </div>
      {!isEmbed ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors shrink-0"
        >
          Download
        </a>
      ) : null}
    </div>
  );
}
